/**
 * SmsInboxPage — Batch #14.229
 *
 * Boîte SMS Twilio : conversations regroupées par numéro (envoyés + reçus).
 * Liste de conversations à gauche, fil de discussion (bulles) à droite,
 * composer de réponse en bas. Bouton « Nouveau SMS » pour initier un envoi.
 *
 * Backend :
 *   GET  /admin/sms/inbox                       → { items, unread_count }
 *   GET  /admin/sms/inbox/config                → { sender, inbound_webhook_url, ... }
 *   POST /admin/sms/inbox/conversation/read     → { number }
 *   POST /admin/sms/send                        → { to, text }
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, RefreshCw, Loader2, Search, Smartphone, Send, Plus, X,
  MessageSquare, Check, CheckCheck, AlertCircle, Copy, User,
  BookmarkPlus, Pencil, Trash2,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString("fr-CA", {
      day: "2-digit", month: "short",
      year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
    });
  } catch { return iso; }
}

function fmtFullDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtPhone(num) {
  // +15145551234 -> (514) 555-1234
  const d = (num || "").replace(/\D/g, "");
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  return num || "—";
}

// Statut d'un message sortant : ✓ envoyé · ✓✓ livré · ✗ échec
function OutboundStatus({ msg }) {
  if (msg.status === "failed") {
    return (
      <span className="inline-flex items-center gap-0.5 text-rose-200" title={msg.error || "Échec d'envoi"}>
        <AlertCircle className="w-3 h-3" /> échec
      </span>
    );
  }
  if (msg.status === "delivered") {
    return <CheckCheck className="w-3.5 h-3.5 text-emerald-200" title="Livré" />;
  }
  return <Check className="w-3.5 h-3.5 text-emerald-200/70" title="Envoyé" />;
}

function ConversationListItem({ conv, isSelected, onClick }) {
  const label = conv.contact_name || fmtPhone(conv.number);
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 transition relative ${
        isSelected ? "bg-emerald-50 border-l-4 border-l-emerald-500" : "hover:bg-slate-50 border-l-4 border-l-transparent"
      }`}
      data-testid={`sms-conv-${conv.number}`}
      data-unread={conv.unread > 0}
    >
      <div className="flex items-baseline justify-between gap-2 mb-0.5">
        <div className={`text-sm truncate flex-1 ${conv.unread > 0 ? "font-bold text-slate-900" : "font-semibold text-slate-700"}`}>
          {label}
        </div>
        <div className={`text-[11px] shrink-0 ${conv.unread > 0 ? "text-emerald-600 font-semibold" : "text-slate-400"}`}>
          {fmtTime(conv.last?.date)}
        </div>
      </div>
      {conv.contact_name && (
        <div className="text-[11px] text-slate-400 font-mono">{fmtPhone(conv.number)}</div>
      )}
      <div className="flex items-center gap-2 mt-0.5">
        <div className={`text-xs truncate flex-1 ${conv.unread > 0 ? "font-semibold text-slate-800" : "text-slate-500"}`}>
          {conv.last?.direction === "outbound" && <span className="text-slate-400">Vous : </span>}
          {conv.last?.body || "(vide)"}
        </div>
        {conv.unread > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold shrink-0"
            data-testid={`sms-conv-unread-${conv.number}`}
          >
            {conv.unread}
          </span>
        )}
      </div>
    </button>
  );
}

// Fil de conversation : bulles entrantes (gauche, gris) / sortantes (droite, vert)
function ConversationThread({ conv }) {
  const endRef = useRef(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "auto" });
  }, [conv?.number, conv?.messages?.length]);

  if (!conv) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-slate-400 px-6 text-center">
        <MessageSquare className="w-12 h-12 mb-3 opacity-40" />
        <p className="text-sm">Sélectionnez une conversation</p>
        <p className="text-xs mt-1">ou cliquez « Nouveau SMS » pour écrire à un numéro</p>
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-2 bg-slate-50/60" data-testid="sms-thread">
      {conv.messages.map((m) => (
        <div
          key={`${m.direction}-${m.id}`}
          className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}
          data-testid={`sms-msg-${m.id}`}
        >
          <div
            className={`max-w-[75%] rounded-2xl px-3.5 py-2 shadow-sm ${
              m.direction === "outbound"
                ? "bg-emerald-600 text-white rounded-br-sm"
                : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
            }`}
          >
            <div className="text-sm whitespace-pre-wrap break-words">{m.body || "(vide)"}</div>
            <div className={`flex items-center gap-1.5 mt-1 text-[10px] ${
              m.direction === "outbound" ? "text-emerald-100/90 justify-end" : "text-slate-400"
            }`}>
              {fmtFullDate(m.date)}
              {m.direction === "outbound" && <OutboundStatus msg={m} />}
            </div>
            {m.direction === "outbound" && m.status === "failed" && m.error && (
              <div className="text-[10px] text-rose-100 bg-rose-500/40 rounded px-1.5 py-0.5 mt-1">
                {m.error}
              </div>
            )}
          </div>
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}

// Chips de modèles SMS — insèrent le texte dans le champ cible (Batch #14.231)
function TemplateChips({ templates, onPick, testIdPrefix }) {
  if (!templates?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-template-chips`}>
      {templates.map((t) => (
        <button
          key={t.id}
          onClick={() => onPick(t.text)}
          className="px-2.5 py-1 text-[11px] rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition"
          title={t.text}
          data-testid={`${testIdPrefix}-template-chip-${t.id}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// Modal de gestion des modèles SMS (CRUD) — Batch #14.231
function SmsTemplatesModal({ templates, onClose, onChanged }) {
  const [label, setLabel] = useState("");
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setLabel(""); setText(""); setEditingId(null); };

  const save = async () => {
    if (!label.trim() || !text.trim()) {
      toast.error("Nom et texte requis");
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/sms/templates/${editingId}`, { label: label.trim(), text: text.trim() }, { headers: authH() });
        toast.success("Modèle modifié");
      } else {
        await axios.post(`${API}/admin/sms/templates`, { label: label.trim(), text: text.trim() }, { headers: authH() });
        toast.success("Modèle créé");
      }
      reset();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement du modèle");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/sms/templates/${id}`, { headers: authH() });
      toast.success("Modèle supprimé");
      if (editingId === id) reset();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de suppression");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="sms-templates-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50 rounded-t-2xl">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <BookmarkPlus className="w-4 h-4 text-emerald-600" /> Modèles de SMS
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded" data-testid="sms-templates-modal-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1">
          {/* Liste */}
          {templates.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-2">Aucun modèle — créez le premier ci-dessous.</p>
          ) : (
            <div className="space-y-1.5">
              {templates.map((t) => (
                <div key={t.id} className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2"
                     data-testid={`sms-template-row-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-slate-800">{t.label}</div>
                    <div className="text-[11px] text-slate-500 line-clamp-2">{t.text}</div>
                  </div>
                  <button onClick={() => { setEditingId(t.id); setLabel(t.label); setText(t.text); }}
                          className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400" title="Modifier"
                          data-testid={`sms-template-edit-${t.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(t.id)}
                          className="p-1.5 rounded-lg hover:bg-rose-100 text-rose-400" title="Supprimer"
                          data-testid={`sms-template-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* Formulaire ajout/édition */}
          <div className="border-t pt-3 space-y-2">
            <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
              {editingId ? "Modifier le modèle" : "Nouveau modèle"}
            </div>
            <input
              value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60}
              placeholder="Nom court (ex : Confirmation RDV)"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-emerald-400"
              data-testid="sms-template-form-label"
            />
            <textarea
              rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={900}
              placeholder="Texte du SMS…"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-emerald-400 resize-none"
              data-testid="sms-template-form-text"
            />
            <div className="flex justify-end gap-2">
              {editingId && (
                <button onClick={reset} className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 hover:bg-slate-50">
                  Annuler l'édition
                </button>
              )}
              <button
                onClick={save} disabled={busy || !label.trim() || !text.trim()}
                className="px-4 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid="sms-template-form-save"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {editingId ? "Enregistrer" : "Créer le modèle"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal « Nouveau SMS »
function NewSmsModal({ onClose, onSent, templates }) {
  const [to, setTo] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!to.trim() || !text.trim()) {
      toast.error("Numéro et message requis");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/sms/send`, { to: to.trim(), text: text.trim() }, { headers: authH() });
      toast.success("SMS envoyé");
      onSent?.(r.data?.to || to.trim());
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi SMS");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="sms-new-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50 rounded-t-2xl">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-emerald-600" /> Nouveau SMS
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded" data-testid="sms-new-modal-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider block mb-1">
              Numéro destinataire
            </label>
            <input
              type="tel"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="ex : 514-555-1234 ou +15145551234"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono focus:outline-none focus:border-emerald-400"
              data-testid="sms-new-to"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider block mb-1">
              Message <span className="font-normal normal-case text-slate-400">({text.length}/900)</span>
            </label>
            <TemplateChips templates={templates} onPick={setText} testIdPrefix="sms-new" />
            <textarea
              rows={4}
              maxLength={900}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Votre message…"
              className="w-full px-3 py-2 mt-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-emerald-400 resize-none"
              data-testid="sms-new-text"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 rounded-b-2xl flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-100">
            Annuler
          </button>
          <button
            onClick={send}
            disabled={sending || !to.trim() || !text.trim()}
            className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="sms-new-send"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SmsInboxPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [reply, setReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  // Batch #14.231 — Modèles SMS
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/sms/templates`, { headers: authH() });
      setTemplates(r.data?.items || []);
    } catch { /* non bloquant */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sms/inbox?limit=500`, { headers: authH() });
      setItems(r.data?.items || []);
      setUnreadCount(r.data?.unread_count || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement de la boîte SMS");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/sms/inbox/config`, { headers: authH() });
      setConfig(r.data);
    } catch { /* non bloquant */ }
  }, []);

  useEffect(() => { load(); loadConfig(); loadTemplates(); }, [load, loadConfig, loadTemplates]);

  // Regroupement par numéro → conversations
  const conversations = useMemo(() => {
    const map = new Map();
    for (const m of items) {
      const key = m.number || "inconnu";
      if (!map.has(key)) map.set(key, { number: key, messages: [], contact_name: null, unread: 0, last: null });
      const c = map.get(key);
      c.messages.push(m);
      if (m.contact_name && !c.contact_name) c.contact_name = m.contact_name;
      if (m.direction === "inbound" && !m.is_read) c.unread++;
    }
    for (const c of map.values()) {
      c.messages.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      c.last = c.messages[c.messages.length - 1];
    }
    return [...map.values()].sort((a, b) => (b.last?.date || "").localeCompare(a.last?.date || ""));
  }, [items]);

  const filteredConvs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) =>
      (c.contact_name || "").toLowerCase().includes(q)
      || (c.number || "").includes(q.replace(/\D/g, "") || q)
      || c.messages.some((m) => (m.body || "").toLowerCase().includes(q)),
    );
  }, [conversations, search]);

  const selectedConv = conversations.find((c) => c.number === selectedNumber) || null;

  const openConversation = useCallback(async (conv) => {
    setSelectedNumber(conv.number);
    if (conv.unread > 0) {
      // Marquage lu optimiste + serveur
      setItems((prev) => prev.map((m) =>
        m.number === conv.number && m.direction === "inbound" ? { ...m, is_read: true } : m,
      ));
      setUnreadCount((prev) => Math.max(0, prev - conv.unread));
      try {
        await axios.post(`${API}/admin/sms/inbox/conversation/read`, { number: conv.number }, { headers: authH() });
      } catch { /* best-effort */ }
    }
  }, []);

  const sendReply = async () => {
    if (!reply.trim() || !selectedNumber) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/admin/sms/send`, { to: selectedNumber, text: reply.trim() }, { headers: authH() });
      setReply("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi SMS");
    } finally {
      setSendingReply(false);
    }
  };

  const copyWebhook = () => {
    if (config?.inbound_webhook_url) {
      navigator.clipboard?.writeText(config.inbound_webhook_url);
      toast.success("URL webhook copiée");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-sms-inbox-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button
          onClick={() => navigate("/admin/documents")}
          className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center transition"
          aria-label="Retour"
          data-testid="sms-inbox-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <Smartphone className="w-5 h-5 text-emerald-600" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-slate-900">Boîte SMS</h1>
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold"
                data-testid="sms-inbox-unread-count"
              >
                {unreadCount}
              </span>
            )}
          </div>
          {config?.sender && (
            <div className="text-[11px] text-slate-500 font-mono">{fmtPhone(config.sender)} · Twilio</div>
          )}
        </div>
        {config?.inbound_webhook_url && (
          <button
            onClick={copyWebhook}
            className="hidden sm:inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-50 px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-100"
            title={`URL webhook réception à configurer dans la console Twilio (A MESSAGE COMES IN) :\n${config.inbound_webhook_url}`}
            data-testid="sms-inbox-copy-webhook"
          >
            <Copy className="w-3 h-3" /> Webhook réception
          </button>
        )}
        <button
          onClick={() => setShowTemplates(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200 transition"
          data-testid="sms-inbox-templates-btn"
        >
          <BookmarkPlus className="w-4 h-4" />
          Modèles
        </button>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-emerald-600 hover:bg-emerald-700 text-white transition"
          data-testid="sms-inbox-new-btn"
        >
          <Plus className="w-4 h-4" />
          Nouveau SMS
        </button>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition disabled:opacity-50"
          disabled={loading}
          data-testid="sms-inbox-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Rafraîchir
        </button>
      </header>

      {/* Body : 2 colonnes — conversations + fil */}
      <div className="flex h-[calc(100vh-65px)]">
        <aside className="w-[340px] flex flex-col border-r border-slate-200 bg-white">
          <div className="border-b border-slate-100 p-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom, numéro ou texte…"
                className="w-full pl-8 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:bg-white focus:border-emerald-300"
                data-testid="sms-inbox-search"
              />
            </div>
            <div className="text-[11px] text-slate-400 mt-2 px-1">
              {filteredConvs.length} conversation{filteredConvs.length > 1 ? "s" : ""}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="sms-inbox-list">
            {loading && items.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : filteredConvs.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">
                {search ? "Aucun résultat" : "Aucun SMS pour l'instant"}
              </div>
            ) : (
              filteredConvs.map((c) => (
                <ConversationListItem
                  key={c.number}
                  conv={c}
                  isSelected={selectedNumber === c.number}
                  onClick={() => openConversation(c)}
                />
              ))
            )}
          </div>
        </aside>

        {/* Fil + composer */}
        <main className="flex-1 flex flex-col bg-white overflow-hidden">
          {selectedConv && (
            <div className="border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center gap-3 bg-white">
              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
              <div>
                <div className="font-bold text-slate-900 text-sm" data-testid="sms-thread-title">
                  {selectedConv.contact_name || fmtPhone(selectedConv.number)}
                </div>
                <div className="text-[11px] text-slate-400 font-mono">
                  {fmtPhone(selectedConv.number)}
                  {selectedConv.contact_kind === "tenant" && " · Locataire"}
                  {selectedConv.contact_kind === "client" && " · Client"}
                </div>
              </div>
            </div>
          )}
          <ConversationThread conv={selectedConv} />
          {selectedConv && (
            <div className="border-t border-slate-200 p-3 bg-white space-y-2">
              <TemplateChips templates={templates} onPick={setReply} testIdPrefix="sms-reply" />
              <div className="flex items-end gap-2">
                <textarea
                  rows={1}
                  maxLength={900}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }
                  }}
                  placeholder="Répondre par SMS… (Entrée pour envoyer)"
                  className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-xl focus:outline-none focus:border-emerald-400 resize-none"
                  data-testid="sms-reply-input"
                />
                <button
                  onClick={sendReply}
                  disabled={sendingReply || !reply.trim()}
                  className="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5 text-sm"
                  data-testid="sms-reply-send"
                >
                  {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {showNew && (
        <NewSmsModal
          templates={templates}
          onClose={() => setShowNew(false)}
          onSent={(num) => { load().then(() => setSelectedNumber(num)); }}
        />
      )}
      {showTemplates && (
        <SmsTemplatesModal
          templates={templates}
          onClose={() => setShowTemplates(false)}
          onChanged={loadTemplates}
        />
      )}
    </div>
  );
}
